import React, { useEffect, useState } from "react";
import { I18nProvider, useI18n } from "./i18n/I18nContext";
import { LANGUAGES } from "./i18n/translations";
import { LOGO, HERO_IMAGE, ROOMS_DATA, CASA_PHOTOS, TOUR_IMAGES, CONTACT } from "./data/assets";
import { REVIEWS, REVIEW_LINKS } from "./data/reviews";
import { AMENITIES, ACCESSIBILITY_NOTE } from "./data/amenities";
import { PRIVACY_POLICY, COOKIE_POLICY, OWNER } from "./data/policies";
import axios from "axios";
import "./App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Helpers ---------- */
const useScroll = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  return scrolled;
};

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".fade-up");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); });
    }, { threshold: 0.12 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  });
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ---------- Reusable Wordmark (I + lightning M + aestri) ---------- */
const MaestriWordmark = ({ variant = "light", showStars = true, className = "" }) => (
  <span className={`wordmark wm-${variant} ${className}`} aria-label="I Maestri">
    {showStars && (
      <span className="wm-stars" aria-hidden="true">
        <i className="fa-solid fa-star wm-star wm-star-3"></i>
        <i className="fa-solid fa-star wm-star wm-star-2"></i>
        <i className="fa-solid fa-star wm-star wm-star-1"></i>
      </span>
    )}
    <span className="wm-row" aria-hidden="true">
      <span className="wm-letter">I</span>
      <svg className="wm-bolt" viewBox="0 0 120 230" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        {/* yellow/gold diagonal accent above */}
        <line
          x1="6" y1="60" x2="96" y2="8"
          stroke="#E9B340"
          strokeWidth="11"
          strokeLinecap="round"
        />
        {/* red lightning-M drawn as an OPEN zigzag path (not closed) */}
        <path
          d="M 102 22 L 32 112 L 96 112 L 18 220"
          fill="none"
          stroke="#D81E2A"
          strokeWidth="11"
          strokeLinejoin="miter"
          strokeMiterlimit="3"
          strokeLinecap="round"
        />
      </svg>
      <span className="wm-letter wm-letter-rest">aestri</span>
    </span>
  </span>
);

/* ---------- Policy Modal (Privacy / Cookie) ---------- */
const PolicyModal = ({ policy, onClose }) => {
  const { t, lang } = useI18n();
  useEffect(() => {
    if (!policy) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [policy, onClose]);
  if (!policy) return null;
  const data = policy[lang] || policy.en;
  return (
    <div className="policy-overlay" onClick={onClose} data-testid="policy-modal">
      <div className="policy-modal" onClick={(e) => e.stopPropagation()}>
        <button className="policy-close" onClick={onClose} aria-label={t.cookie.close} data-testid="policy-close">
          <i className="fa-solid fa-xmark"></i>
        </button>
        <h2 className="policy-title">{data.title}</h2>
        <p className="policy-updated">{data.updated}</p>
        <div className="policy-body">
          {data.sections.map((s, i) => (
            <section key={i}>
              <h3>{s.h}</h3>
              <p>{s.p}</p>
            </section>
          ))}
          <section className="policy-owner">
            <p>
              <strong>{OWNER.name}</strong><br />
              {OWNER.address}<br />
              Email: {OWNER.email} · Tel: {OWNER.phone}<br />
              P.IVA: {OWNER.piva} · C.F.: {OWNER.cf}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

/* ---------- Cookie Banner + GA4 (Consent Mode v2) ---------- */
const COOKIE_KEY = "imaestri_cookie_consent_v1";
const GA4_ID = process.env.REACT_APP_GA4_ID || "";

// Initialize gtag with default consent = denied (GDPR compliant default)
const initGtag = () => {
  if (typeof window === "undefined") return;
  if (window.__gtagInit) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500
  });
  window.gtag("js", new Date());
  window.__gtagInit = true;
};

const loadGA4 = (consent) => {
  if (!GA4_ID) return; // no ID configured -> no tracking
  initGtag();
  // Update consent based on user choice
  window.gtag("consent", "update", {
    analytics_storage: consent === "all" ? "granted" : "denied",
    ad_storage: consent === "all" ? "granted" : "denied",
    ad_user_data: consent === "all" ? "granted" : "denied",
    ad_personalization: consent === "all" ? "granted" : "denied"
  });
  // Inject GA4 script only once
  if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA4_ID}"]`)) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
    window.gtag("config", GA4_ID, { anonymize_ip: true });
  }
};

const CookieBanner = ({ onOpenPolicy }) => {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    initGtag();
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      setVisible(true);
    } else {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.value) loadGA4(parsed.value);
      } catch (e) {
        localStorage.removeItem(COOKIE_KEY);
        setVisible(true);
      }
    }
  }, []);
  const handleConsent = (value) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ value, ts: new Date().toISOString() }));
    loadGA4(value);
    setVisible(false);
  };
  if (!visible) return null;
  return (
    <div className="cookie-banner" role="dialog" aria-label="Cookie consent" data-testid="cookie-banner">
      <div className="cookie-banner-inner">
        <div className="cookie-banner-text">
          <h3>{t.cookie.title}</h3>
          <p>
            {t.cookie.body}{" "}
            <button type="button" className="cookie-link" onClick={() => onOpenPolicy("privacy")} data-testid="cookie-link-privacy">{t.cookie.privacyLink}</button>{" "}
            {t.cookie.and}{" "}
            <button type="button" className="cookie-link" onClick={() => onOpenPolicy("cookie")} data-testid="cookie-link-cookie">{t.cookie.cookieLink}</button>.
          </p>
        </div>
        <div className="cookie-banner-actions">
          <button className="btn btn-outline btn-sm" onClick={() => handleConsent("necessary")} data-testid="cookie-reject">{t.cookie.reject}</button>
          <button className="btn btn-gold btn-sm" onClick={() => handleConsent("all")} data-testid="cookie-accept">{t.cookie.accept}</button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Lightbox Gallery ---------- */
const Lightbox = ({ room, onClose }) => {
  const { t } = useI18n();
  const [idx, setIdx] = useState(0);
  const photos = room?.gallery || [];

  useEffect(() => {
    if (!room) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [room, photos.length, onClose]);

  if (!room) return null;

  const prev = (e) => { e.stopPropagation(); setIdx((i) => (i - 1 + photos.length) % photos.length); };
  const next = (e) => { e.stopPropagation(); setIdx((i) => (i + 1) % photos.length); };

  return (
    <div className="lightbox" onClick={onClose} data-testid="lightbox">
      <button className="lightbox-close" onClick={onClose} aria-label={t.rooms.close} data-testid="lightbox-close">
        <i className="fa-solid fa-xmark"></i>
      </button>
      <button className="lightbox-arrow lightbox-prev" onClick={prev} aria-label={t.rooms.prev} data-testid="lightbox-prev">
        <i className="fa-solid fa-chevron-left"></i>
      </button>
      <div className="lightbox-stage" onClick={(e) => e.stopPropagation()}>
        <img src={photos[idx]} alt={`${room.name} ${idx + 1}`} data-testid="lightbox-image" />
        <div className="lightbox-meta">
          <span className="lightbox-room">{room.name}</span>
          <span className="lightbox-counter">{idx + 1} / {photos.length}</span>
        </div>
        <div className="lightbox-thumbs">
          {photos.map((p, i) => (
            <button key={i} className={`lightbox-thumb ${i === idx ? "active" : ""}`} onClick={() => setIdx(i)} data-testid={`lightbox-thumb-${i}`}>
              <img src={p} alt={`thumb ${i + 1}`} />
            </button>
          ))}
        </div>
      </div>
      <button className="lightbox-arrow lightbox-next" onClick={next} aria-label={t.rooms.next} data-testid="lightbox-next">
        <i className="fa-solid fa-chevron-right"></i>
      </button>
    </div>
  );
};

/* ---------- Nav ---------- */
const Nav = () => {
  const { t, lang, setLang } = useI18n();
  const scrolled = useScroll();
  const [open, setOpen] = useState(false);

  const linkClick = (id) => { setOpen(false); scrollTo(id); };

  return (
    <header className={`nav ${scrolled ? "scrolled" : ""} ${open ? "mobile-open" : ""}`} data-testid="main-nav">
      <div className="container-x nav-inner">
        <a href="#home" onClick={(e) => { e.preventDefault(); scrollTo("home"); }} className="nav-logo" data-testid="nav-logo">
          <img src={LOGO} alt="B&B I Maestri" />
          <div className="nav-logo-text-wrap">
            <MaestriWordmark variant="dark" showStars={false} className="wm-nav" />
            <div className="nav-logo-sub">Napoli</div>
          </div>
        </a>
        <nav className="nav-links">
          <a href="#bb" onClick={(e) => { e.preventDefault(); linkClick("bb"); }} data-testid="nav-bb">{t.nav.bb}</a>
          <a href="#casa" onClick={(e) => { e.preventDefault(); linkClick("casa"); }} data-testid="nav-casa">{t.nav.casa}</a>
          <a href="#tours" onClick={(e) => { e.preventDefault(); linkClick("tours"); }} data-testid="nav-tours">{t.nav.tours}</a>
          <a href="#contact" onClick={(e) => { e.preventDefault(); linkClick("contact"); }} data-testid="nav-contact">{t.nav.contact}</a>
          <div className="lang-switch" data-testid="lang-switch">
            {LANGUAGES.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)} className={lang === l.code ? "active" : ""} data-testid={`lang-${l.code}`}>{l.label}</button>
            ))}
          </div>
          <button className="btn btn-gold" onClick={() => scrollTo("book")} data-testid="nav-book-btn">{t.nav.book}</button>
        </nav>
        <button className="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
          <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`}></i>
        </button>
      </div>
    </header>
  );
};

/* ---------- Hero ---------- */
const Hero = () => {
  const { t } = useI18n();
  return (
    <section id="home" className="hero" data-testid="hero">
      <div className="hero-bg"><img src={HERO_IMAGE} alt="" /></div>
      <div className="container-x hero-inner">
        <div className="fade-up">
          <span className="eyebrow hero-eyebrow">{t.hero.eyebrow}</span>
          <h1 className="hero-title-wrap">
            <MaestriWordmark variant="light" showStars={true} />
          </h1>
          <p className="hero-sub">{t.hero.subtitle}</p>
          <div className="hero-cta">
            <button className="btn btn-gold" onClick={() => scrollTo("bb")} data-testid="hero-cta-bb">{t.hero.cta1} →</button>
            <button className="btn btn-outline" style={{ color: "var(--c-ivory)" }} onClick={() => scrollTo("casa")} data-testid="hero-cta-casa">{t.hero.cta2}</button>
          </div>
        </div>
        <div className="hero-side fade-up">
          <span className="hero-tag">Via dei Tribunali · 257</span>
          <div className="hero-meta">"Una vita senza ricerca non è degna per l'uomo di essere vissuta" — Platone</div>
        </div>
      </div>
      <div className="hero-scroll">↓ {t.hero.scroll}</div>
    </section>
  );
};

/* ---------- Intro ---------- */
const Intro = () => {
  const { t } = useI18n();
  return (
    <section className="section intro">
      <div className="container-x intro-grid">
        <div className="fade-up">
          <span className="eyebrow">— {t.intro.label}</span>
          <h2 className="font-display" style={{ marginTop: 18 }}>{t.intro.title}</h2>
        </div>
        <div className="fade-up">
          <p>{t.intro.body}</p>
          <p className="intro-esoteric">{t.intro.bodyExtra}</p>
        </div>
      </div>
    </section>
  );
};

/* ---------- Properties ---------- */
const Properties = () => {
  const { t } = useI18n();
  return (
    <section className="section props">
      <div className="container-x">
        <div className="s-head fade-up">
          <span className="eyebrow">— I Maestri</span>
          <h2>{t.properties.title}</h2>
        </div>
        <div className="props-grid">
          <div className="prop-card fade-up" data-testid="prop-card-bb">
            <img src={ROOMS_DATA.platone.cover} alt="B&B I Maestri" />
            <div className="prop-content">
              <span className="prop-tag">{t.properties.bb.tag}</span>
              <h3>{t.properties.bb.title}</h3>
              <p>{t.properties.bb.desc}</p>
              <div className="prop-actions">
                <button className="btn btn-gold" onClick={() => scrollTo("bb")}>{t.properties.bb.cta}</button>
                <a className="btn btn-outline" style={{ color: "white", borderColor: "white" }} href={CONTACT.bookingUrl} target="_blank" rel="noopener" data-testid="bb-booking-link">{t.properties.bb.booking}</a>
              </div>
            </div>
          </div>
          <div className="prop-card fade-up" data-testid="prop-card-casa">
            <img src={CASA_PHOTOS[0]} alt="La Casa dei Maestri" />
            <div className="prop-content">
              <span className="prop-tag">{t.properties.casa.tag}</span>
              <h3>{t.properties.casa.title}</h3>
              <p>{t.properties.casa.desc}</p>
              <div className="prop-actions">
                <button className="btn btn-gold" onClick={() => scrollTo("casa")}>{t.properties.casa.cta}</button>
                <a className="btn btn-outline" style={{ color: "white", borderColor: "white" }} href={CONTACT.airbnbUrl} target="_blank" rel="noopener noreferrer" data-testid="casa-airbnb-link">{t.properties.casa.airbnb}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Rooms ---------- */
const Rooms = ({ onOpenGallery }) => {
  const { t } = useI18n();
  return (
    <section id="bb" className="section">
      <div className="container-x">
        <div className="s-head fade-up">
          <span className="eyebrow">— B&B · 3° piano</span>
          <h2>{t.rooms.title}</h2>
          <p>{t.rooms.subtitle}</p>
        </div>
        <div className="rooms-grid">
          {t.rooms.list.map((room, i) => {
            const data = ROOMS_DATA[room.id];
            return (
              <div key={room.id} className="room-card fade-up" data-testid={`room-${room.id}`}>
                <button
                  type="button"
                  className="room-cover room-cover-btn"
                  onClick={() => onOpenGallery({ ...data, name: room.name })}
                  data-testid={`room-${room.id}-cover`}
                  aria-label={`${t.rooms.gallery} ${room.name}`}
                >
                  <img src={data.cover} alt={room.name} />
                  <span className="room-num">0{i + 1}</span>
                  <span className="room-cover-overlay">
                    <i className="fa-solid fa-expand"></i>
                    <span>{t.rooms.gallery} · {data.gallery.length} {t.rooms.photos}</span>
                  </span>
                </button>
                <div className="room-body">
                  <h3>{room.name}</h3>
                  <div className="room-quote">{room.quote}</div>
                  <div className="room-author">— {room.author}</div>
                  <p className="room-desc">{room.desc}</p>
                  <div className="room-actions">
                    <button className="btn btn-outline btn-sm" onClick={() => onOpenGallery({ ...data, name: room.name })} data-testid={`room-${room.id}-gallery`}>
                      <i className="fa-solid fa-images"></i> {t.rooms.gallery}
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => scrollTo("book")} data-testid={`room-${room.id}-book`}>{t.rooms.cta}</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- Casa ---------- */
const Casa = ({ onOpenGallery }) => {
  const { t } = useI18n();
  const casaRoom = { name: t.casa.title, gallery: CASA_PHOTOS, cover: CASA_PHOTOS[0] };
  return (
    <section id="casa" className="section casa">
      <div className="container-x casa-grid">
        <div className="casa-images fade-up">
          {CASA_PHOTOS.slice(0, 4).map((src, i) => (
            <button
              type="button"
              key={i}
              className="casa-img-btn"
              onClick={() => onOpenGallery(casaRoom)}
              aria-label={`${t.rooms.gallery} — ${t.casa.title}`}
              data-testid={`casa-photo-${i}`}
            >
              <img src={src} alt={`Casa ${i + 1}`} />
            </button>
          ))}
        </div>
        <div className="fade-up">
          <span className="eyebrow casa-tag">— Casa Vacanze · 1° piano</span>
          <h2>{t.casa.title}</h2>
          <p className="casa-sub">{t.casa.subtitle}</p>
          <p>{t.casa.desc}</p>
          <div className="casa-features">
            {t.casa.features.map((f, i) => (<div key={i} className="casa-feature">{f}</div>))}
          </div>
          <div className="casa-actions">
            <button className="btn btn-outline btn-sm" style={{ color: "var(--c-ivory)", borderColor: "var(--c-ivory)" }} onClick={() => onOpenGallery(casaRoom)} data-testid="casa-gallery">
              <i className="fa-solid fa-images"></i> {t.rooms.gallery} · {CASA_PHOTOS.length} {t.rooms.photos}
            </button>
            <a href={CONTACT.airbnbUrl} target="_blank" rel="noopener noreferrer" className="btn btn-gold" data-testid="casa-airbnb-cta">{t.casa.airbnb}</a>
            <button className="btn btn-outline" style={{ color: "var(--c-ivory)", borderColor: "var(--c-ivory)" }} onClick={() => scrollTo("book")} data-testid="casa-direct-cta">{t.casa.direct}</button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Tours ---------- */
const Tours = () => {
  const { t } = useI18n();
  return (
    <section id="tours" className="section tours">
      <div className="container-x">
        <div className="s-head fade-up">
          <span className="eyebrow">— Esperienze</span>
          <h2>{t.tours.title}</h2>
          <p>{t.tours.subtitle}</p>
        </div>
        <div className="tours-grid">
          {t.tours.list.map((tour, i) => (
            <div key={i} className="tour-card fade-up" data-testid={`tour-${i}`}>
              <div className="tour-cover"><img src={TOUR_IMAGES[i]} alt={tour.title} /></div>
              <div className="tour-body">
                <div className="tour-duration">{tour.duration}</div>
                <h3>{tour.title}</h3>
                <p>{tour.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 40 }} className="fade-up">
          <button className="btn btn-primary" onClick={() => scrollTo("book")} data-testid="tours-cta">{t.tours.cta} →</button>
        </div>
      </div>
    </section>
  );
};

/* ---------- Benefits ---------- */
const Benefits = () => {
  const { t } = useI18n();
  const icons = { tag: "fa-tag", heart: "fa-heart", phone: "fa-phone", map: "fa-map-location-dot" };
  return (
    <section className="section benefits">
      <div className="container-x">
        <div className="s-head fade-up">
          <span className="eyebrow">— Prenotazione diretta</span>
          <h2>{t.benefits.title}</h2>
          <p>{t.benefits.subtitle}</p>
        </div>
        <div className="benefits-grid">
          {t.benefits.items.map((b, i) => (
            <div key={i} className="benefit-card fade-up" data-testid={`benefit-${i}`}>
              <div className="benefit-icon"><i className={`fa-solid ${icons[b.icon]}`}></i></div>
              <h4>{b.title}</h4>
              <p>{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Booking form ---------- */
const Booking = () => {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ property: "bb", room: "", guest_name: "", guest_email: "", guest_phone: "", check_in: "", check_out: "", guests: 2, message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "sending", msg: "" });
    try {
      await axios.post(`${API}/bookings`, { ...form, language: lang });
      setStatus({ state: "success", msg: t.booking.success });
      // open mailto with prefilled body
      const body = `Nuova richiesta da: ${form.guest_name}%0A%0AStruttura: ${form.property === "bb" ? "B&B I Maestri" : "Casa dei Maestri"}%0ACamera: ${form.room || "-"}%0ACheck-in: ${form.check_in}%0ACheck-out: ${form.check_out}%0AOspiti: ${form.guests}%0AEmail: ${form.guest_email}%0ATelefono: ${form.guest_phone || "-"}%0A%0ANote: ${form.message || "-"}`;
      const mailto = `mailto:${CONTACT.email}?subject=Richiesta%20prenotazione%20-%20${form.guest_name}&body=${body}`;
      window.location.href = mailto;
      setForm({ property: "bb", room: "", guest_name: "", guest_email: "", guest_phone: "", check_in: "", check_out: "", guests: 2, message: "" });
    } catch (err) {
      setStatus({ state: "error", msg: t.booking.error });
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section id="book" className="section booking">
      <div className="container-x booking-wrap">
        <div className="booking-info fade-up">
          <span className="eyebrow">— {t.nav.book}</span>
          <h2>{t.booking.title}</h2>
          <p>{t.booking.subtitle}</p>
          <div className="booking-direct">
            <h4>{t.contact.title}</h4>
            <div className="booking-direct-row"><i className="fa-solid fa-location-dot"></i><span>{t.contact.address}</span></div>
            <div className="booking-direct-row"><i className="fa-solid fa-phone"></i><a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-phone">{CONTACT.phone}</a></div>
            <div className="booking-direct-row"><i className="fa-brands fa-whatsapp"></i><a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp">{t.contact.whatsapp}</a></div>
            <div className="booking-direct-row"><i className="fa-solid fa-envelope"></i><a href={`mailto:${CONTACT.email}`} data-testid="contact-email">{CONTACT.email}</a></div>
          </div>
        </div>
        <form className="booking-form fade-up" onSubmit={submit} data-testid="booking-form">
          <div className="form-row">
            <div className="form-field">
              <label>{t.booking.property}</label>
              <select value={form.property} onChange={(e) => upd("property", e.target.value)} data-testid="form-property" required>
                <option value="bb">{t.booking.bb}</option>
                <option value="casa">{t.booking.casa}</option>
              </select>
            </div>
            <div className="form-field">
              <label>{t.booking.room}</label>
              <select value={form.room} onChange={(e) => upd("room", e.target.value)} data-testid="form-room" disabled={form.property === "casa"}>
                <option value="">{t.booking.anyRoom}</option>
                {t.rooms.list.map((r) => (<option key={r.id} value={r.name}>{r.name}</option>))}
              </select>
            </div>
          </div>
          <div className="form-row single">
            <div className="form-field">
              <label>{t.booking.name}</label>
              <input type="text" required value={form.guest_name} onChange={(e) => upd("guest_name", e.target.value)} data-testid="form-name" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label>{t.booking.email}</label>
              <input type="email" required value={form.guest_email} onChange={(e) => upd("guest_email", e.target.value)} data-testid="form-email" />
            </div>
            <div className="form-field">
              <label>{t.booking.phone}</label>
              <input type="tel" value={form.guest_phone} onChange={(e) => upd("guest_phone", e.target.value)} data-testid="form-phone" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label>{t.booking.checkin}</label>
              <input type="date" required min={today} value={form.check_in} onChange={(e) => upd("check_in", e.target.value)} data-testid="form-checkin" />
            </div>
            <div className="form-field">
              <label>{t.booking.checkout}</label>
              <input type="date" required min={form.check_in || today} value={form.check_out} onChange={(e) => upd("check_out", e.target.value)} data-testid="form-checkout" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label>{t.booking.guests}</label>
              <input type="number" min="1" max="6" required value={form.guests} onChange={(e) => upd("guests", parseInt(e.target.value))} data-testid="form-guests" />
            </div>
          </div>
          <div className="form-row single">
            <div className="form-field">
              <label>{t.booking.message}</label>
              <textarea value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="form-message" />
            </div>
          </div>
          <button type="submit" className="form-submit" disabled={status.state === "sending"} data-testid="form-submit">
            {status.state === "sending" ? t.booking.sending : t.booking.submit}
          </button>
          {status.state === "success" && <div className="form-msg success" data-testid="form-success">{status.msg}</div>}
          {status.state === "error" && <div className="form-msg error" data-testid="form-error">{status.msg}</div>}
          <div className="form-alt">
            <span>{t.booking.orCall}: <a href={`tel:${CONTACT.phoneRaw}`}>{CONTACT.phone}</a></span>
            <span>·</span>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer">{t.booking.orWa}</a>
          </div>
        </form>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
const Footer = ({ onOpenPolicy }) => {
  const { t } = useI18n();
  return (
    <footer id="contact" className="footer">
      <div className="container-x">
        <div className="footer-grid">
          <div className="footer-brand">
            <img src={LOGO} alt="I Maestri" />
            <p>{t.footer.tagline}</p>
          </div>
          <div>
            <h5>{t.footer.explore}</h5>
            <ul>
              <li><a href="#bb" onClick={(e) => { e.preventDefault(); scrollTo("bb"); }}>{t.nav.bb}</a></li>
              <li><a href="#casa" onClick={(e) => { e.preventDefault(); scrollTo("casa"); }}>{t.nav.casa}</a></li>
              <li><a href="#tours" onClick={(e) => { e.preventDefault(); scrollTo("tours"); }}>{t.nav.tours}</a></li>
              <li><a href="#book" onClick={(e) => { e.preventDefault(); scrollTo("book"); }}>{t.footer.direct}</a></li>
            </ul>
          </div>
          <div className="footer-contact">
            <h5>{t.footer.contact}</h5>
            <div><i className="fa-solid fa-location-dot"></i><span>{t.contact.address}<br />{t.contact.floors}</span></div>
            <div><i className="fa-solid fa-phone"></i><a href={`tel:${CONTACT.phoneRaw}`}>{CONTACT.phone}</a></div>
            <div><i className="fa-brands fa-whatsapp"></i><a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer">WhatsApp</a></div>
            <div><i className="fa-solid fa-envelope"></i><a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></div>
          </div>
          <div>
            <h5>{t.footer.partners}</h5>
            <div className="footer-partners" style={{ flexDirection: "column", gap: 10 }}>
              <a href={CONTACT.bookingUrl} target="_blank" rel="noopener"><i className="fa-solid fa-bed"></i> Booking.com</a>
              <a href={CONTACT.airbnbUrl} target="_blank" rel="noopener noreferrer"><i className="fa-solid fa-house"></i> Airbnb</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 B&B I Maestri Napoli · {t.footer.legal} · Via dei Tribunali 257 · 80139 Napoli · P.IVA 02466610462 · C.F. LMNVLR71T56F839W</span>
          <div className="footer-legal-links">
            <button type="button" className="footer-legal-link" onClick={() => onOpenPolicy("privacy")} data-testid="footer-privacy">{t.cookie.privacyLink}</button>
            <span aria-hidden="true">·</span>
            <button type="button" className="footer-legal-link" onClick={() => onOpenPolicy("cookie")} data-testid="footer-cookie">{t.cookie.cookieLink}</button>
            <span aria-hidden="true">·</span>
            <button type="button" className="footer-legal-link" onClick={() => { localStorage.removeItem("imaestri_cookie_consent_v1"); window.location.reload(); }} data-testid="footer-cookie-prefs">{t.cookie.managePrefs}</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ---------- Amenities ---------- */
const Amenities = () => {
  const { t, lang } = useI18n();
  return (
    <section id="amenities" className="section amenities-section">
      <div className="container-x">
        <div className="s-head fade-up">
          <span className="eyebrow">{t.amenities.eyebrow}</span>
          <h2>{t.amenities.title}</h2>
          <p>{t.amenities.subtitle}</p>
        </div>
        <div className="amenities-grid">
          {AMENITIES.map((a) => (
            <div key={a.id} className="amenity-card fade-up" data-testid={`amenity-${a.id}`}>
              <div className="amenity-head">
                <i className={`fa-solid ${a.icon}`}></i>
                <h3>{a.title[lang] || a.title.en}</h3>
              </div>
              <ul className="amenity-list">
                {(a.items[lang] || a.items.en).map((it, i) => (
                  <li key={i}><i className="fa-solid fa-check"></i> {it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="amenity-accessibility" data-testid="amenity-accessibility">
          <i className="fa-solid fa-circle-info"></i> <strong>{t.amenities.accessibility}:</strong> {ACCESSIBILITY_NOTE[lang] || ACCESSIBILITY_NOTE.en}
        </p>
      </div>
    </section>
  );
};

/* ---------- Reviews ---------- */
const Reviews = () => {
  const { t, lang } = useI18n();
  const scoreLabel = (r) => r.source === "booking" ? `${r.score.toFixed(1)} / 10` : `★ ${r.score.toFixed(1)}`;
  const SOURCE_LABELS = { booking: "Booking.com", airbnb: "Airbnb", google: "Google", tripadvisor: "Tripadvisor" };
  const sourceLabel = (s) => SOURCE_LABELS[s] || s;
  const sourceClass = (s) => `rv-src-${s}`;

  return (
    <section id="reviews" className="section reviews-section">
      <div className="container-x">
        <div className="s-head fade-up">
          <span className="eyebrow">{t.reviews.eyebrow}</span>
          <h2>{t.reviews.title}</h2>
          <p>{t.reviews.subtitle}</p>
        </div>

        <div className="reviews-summary fade-up" data-testid="reviews-summary">
          <a href={REVIEW_LINKS.booking} target="_blank" rel="noopener" className="rv-summary-card rv-summary-booking" data-testid="reviews-link-booking">
            <span className="rv-summary-label">Booking.com</span>
            <span className="rv-summary-score">9.7<span className="rv-summary-out">/10</span></span>
            <span className="rv-summary-cta">{t.reviews.viewAllBooking} →</span>
          </a>
          <a href={REVIEW_LINKS.airbnb} target="_blank" rel="noopener noreferrer" className="rv-summary-card rv-summary-airbnb" data-testid="reviews-link-airbnb">
            <span className="rv-summary-label">Airbnb</span>
            <span className="rv-summary-score">★ 5.0</span>
            <span className="rv-summary-cta">{t.reviews.viewAllAirbnb} →</span>
          </a>
        </div>

        <div className="reviews-grid">
          {REVIEWS.map((r) => {
            const txt = r[`text_${lang}`] || r.text_en;
            return (
              <article key={r.id} className={`rv-card ${sourceClass(r.source)} fade-up`} data-testid={`review-${r.id}`}>
                <header className="rv-head">
                  <span className={`rv-badge ${sourceClass(r.source)}`}>{sourceLabel(r.source)}</span>
                  <span className="rv-score">{scoreLabel(r)}</span>
                </header>
                <blockquote className="rv-text">{txt}</blockquote>
                <footer className="rv-foot">
                  <span className="rv-author">{r.author}</span>
                  <span className="rv-meta">{r.country} · {r.date}</span>
                </footer>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- App ---------- */
const SiteContent = () => {
  useReveal();
  const [galleryRoom, setGalleryRoom] = useState(null);
  const [openPolicy, setOpenPolicy] = useState(null); // "privacy" | "cookie" | null
  const policyData = openPolicy === "privacy" ? PRIVACY_POLICY : openPolicy === "cookie" ? COOKIE_POLICY : null;
  return (
    <>
      <Nav />
      <Hero />
      <Intro />
      <Properties />
      <Rooms onOpenGallery={setGalleryRoom} />
      <Casa onOpenGallery={setGalleryRoom} />
      <Tours />
      <Benefits />
      <Amenities />
      <Reviews />
      <Booking />
      <Footer onOpenPolicy={setOpenPolicy} />
      <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" className="fab-wa" data-testid="fab-whatsapp" aria-label="WhatsApp">
        <i className="fa-brands fa-whatsapp"></i>
      </a>
      <Lightbox room={galleryRoom} onClose={() => setGalleryRoom(null)} />
      <CookieBanner onOpenPolicy={setOpenPolicy} />
      <PolicyModal policy={policyData} onClose={() => setOpenPolicy(null)} />
    </>
  );
};

function App() {
  return (
    <I18nProvider>
      <SiteContent />
    </I18nProvider>
  );
}

export default App;
